import { useState } from 'react';
import { salesApi, PlatformApiError } from '../lib/platformApi';

/**
 * ═══ ASK THIS SCHOOL'S OWN RECORD ═══
 *
 * ⚠ EVERY CLAIM SHOWS THE LINE IT CAME FROM. Not a footnote, not a tooltip — the quoted line sits
 * under the sentence. A citation nobody can follow is decoration, and this is the only thing that
 * lets you tell a good answer from a confident one.
 *
 * ⚠ AND SUGGESTIONS ARE LABELLED AS SUGGESTIONS. Claims are about the record and are checked against
 * it; next actions are the model's opinion and are not. Showing them the same way would launder one
 * into the other.
 */

interface Claim { text: string; cites: string[]; sources: string[] }
interface Answer {
  claims: Claim[]; nextActions: string[]; cannotAnswer: string | null;
  dropped: string[]; linesConsidered: number;
}
interface Draft {
  template: string; templateLabel: string; text: string;
  refusals: string[]; canShow: boolean; sendNotBefore: string | null; sendNowIsFine: boolean;
}

const SUGGESTED = [
  'What did they object to, and was it ever answered?',
  'What have we promised them that is still open?',
  'Who have we not met yet?',
  'What did they ask for that we have since built?',
];

const TEMPLATES = ['AfterFirstCall', 'DemoConfirmation', 'QuoteSent', 'FollowUp'];

export function AskTheRecord({ id }: { id: string }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const ask = async (q: string) => {
    if (!q.trim()) return;
    setBusy(true); setError(null); setDraft(null);
    try {
      setAnswer(await salesApi.post<Answer>(`/api/platform/sales/prospects/${id}/ask`, { question: q }));
    } catch (e) {
      setError(e instanceof PlatformApiError ? e.message : 'Could not ask.');
    } finally { setBusy(false); }
  };

  const makeDraft = async (template: string) => {
    setBusy(true); setError(null); setAnswer(null); setCopied(false);
    try {
      setDraft(await salesApi.post<Draft>(`/api/platform/sales/prospects/${id}/draft`, { template }));
    } catch (e) {
      setError(e instanceof PlatformApiError ? e.message : 'Could not draft it.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginBottom: 14 }}>
      <div style={{ fontWeight: 650, fontSize: 12.5, marginBottom: 6 }}>Ask the record</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <input className="input" placeholder="What did they object to?" value={question}
               style={{ flex: '1 1 260px' }}
               onChange={(e) => setQuestion(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') void ask(question); }} />
        <button className="btn" disabled={busy || !question.trim()} onClick={() => void ask(question)}>
          {busy ? 'Reading…' : 'Ask'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {SUGGESTED.map((s) => (
          <button key={s} className="btn" style={{ fontSize: 11, padding: '2px 8px' }}
                  disabled={busy} onClick={() => { setQuestion(s); void ask(s); }}>
            {s}
          </button>
        ))}
      </div>

      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 8px' }}>{error}</p>}

      {answer && (
        <div style={{ marginBottom: 12 }}>
          {answer.claims.map((c, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13 }}>{c.text}</div>
              {/* ⚠ THE LINE IT CAME FROM, UNDER THE SENTENCE. A citation nobody can follow is
                  decoration, and this is the only thing that separates a good answer from a
                  confident one. */}
              {c.sources.map((s, j) => (
                <div key={j} style={{ fontSize: 11.5, color: 'var(--ink-3)', fontStyle: 'italic', marginTop: 2 }}>
                  {s}
                </div>
              ))}
            </div>
          ))}

          {answer.cannotAnswer && (
            <p style={{ fontSize: 12.5, color: 'var(--ink-2)', margin: '0 0 8px' }}>{answer.cannotAnswer}</p>
          )}

          {answer.nextActions.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {/* ⚠ LABELLED AS OPINION. Claims are checked against the record; these are not, and
                  showing them the same way would launder one into the other. */}
              <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 3 }}>
                Suggestions — not from the record
              </div>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {answer.nextActions.map((a, i) => (
                  <li key={i} style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 2 }}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          {answer.dropped.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {/* ⚠ Shown, not swallowed. An assistant that silently discards half of every reply is
                  one you have no way to judge. */}
              <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 3 }}>Left out</div>
              {answer.dropped.map((d, i) => (
                <div key={i} style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{d}</div>
              ))}
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
            Answered from {answer.linesConsidered} lines of this school's record, and nothing else.
          </div>
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--line-2)', paddingTop: 10 }}>
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 6 }}>
          {/* ⚠ Said on the screen, because it is the promise the design makes. */}
          Draft a WhatsApp message — it is never sent, only written for you to read and send yourself.
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {TEMPLATES.map((t) => (
            <button key={t} className="btn" style={{ fontSize: 11, padding: '2px 8px' }}
                    disabled={busy} onClick={() => void makeDraft(t)}>
              {t.replace(/([a-z])([A-Z])/g, '$1 $2')}
            </button>
          ))}
        </div>

        {draft && (
          <div style={{ marginTop: 10 }}>
            {draft.refusals.map((r, i) => (
              <p key={i} style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 6px' }}>{r}</p>
            ))}
            {draft.canShow && (
              <>
                <pre style={{
                  whiteSpace: 'pre-wrap', fontSize: 12.5, background: 'var(--surface-2, transparent)',
                  border: '1px solid var(--line)', borderRadius: 8, padding: 10, margin: '0 0 6px',
                  fontFamily: 'inherit',
                }}>{draft.text}</pre>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="btn" onClick={() => {
                    void navigator.clipboard?.writeText(draft.text);
                    setCopied(true);
                  }}>{copied ? 'Copied' : 'Copy'}</button>
                  {/* ⚠ The send window is shown even when now is fine, so it is never a surprise. */}
                  <span style={{ fontSize: 11.5, color: draft.sendNowIsFine ? 'var(--ink-3)' : 'var(--warn)' }}>
                    {draft.sendNowIsFine
                      ? 'A reasonable time to send.'
                      : `Not a good time — wait until ${draft.sendNotBefore?.slice(0, 16).replace('T', ' ')}.`}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
